import React, { useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Download } from 'lucide-react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useAuditorControlInventory } from '../../../hooks/analytics/useAuditorReports.ts';
import { RefreshMeta, StatusChip } from '../controller/shared.tsx';
import type { AuditorControlInventoryRow } from '../../../request/analytics/auditorReports.ts';

/**
 * Report #37 — Control Inventory for Reliance. Read-only auditor view (no
 * edit actions) of every control flagged `external_auditor_reliance = true`,
 * plus an Excel export so the auditor can take the inventory offline. Export
 * mirrors the pattern in `components/Controls.js` (XLSX.utils.json_to_sheet
 * + XLSX.writeFile); wrapped in try/catch so a browser quirk never crashes
 * the page — the table itself always renders regardless of export success.
 */
export const ControlInventoryTable: React.FC<{ rows?: AuditorControlInventoryRow[] }> = ({
  rows: rowsOverride,
}) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useAuditorControlInventory();
  const rows = useMemo(() => rowsOverride ?? data ?? [], [rowsOverride, data]);

  const handleExport = () => {
    try {
      const excelData = rows.map((row) => ({
        'Control ID': row.control_id,
        Title: row.title,
        Type: row.control_type ?? '',
        Frequency: row.frequency ?? '',
        Significance: row.significance ?? '',
        'MRC Control': row.mrc_control ? 'Yes' : 'No',
        Status: row.status ?? '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [
        { wch: 15 },
        { wch: 35 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 12 },
        { wch: 14 },
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Control Inventory');
      const timestamp = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `SOX_Control_Inventory_Export_${timestamp}.xlsx`);
    } catch (e) {
      // Non-crashing: export is a convenience action, not core functionality.
      // eslint-disable-next-line no-console
      console.error('Control inventory export failed', e);
    }
  };

  const columns = useMemo<TableColumnDef<AuditorControlInventoryRow>[]>(
    () => [
      {
        id: 'control_id',
        columnLabel: 'Control',
        header: () => 'Control',
        cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.control_id}</span>,
        widthPercent: 12,
      },
      {
        id: 'title',
        columnLabel: 'Title',
        header: () => 'Title',
        cell: ({ row }) => row.original.title,
        widthPercent: 28,
      },
      {
        id: 'control_type',
        columnLabel: 'Type',
        header: () => 'Type',
        cell: ({ row }) => row.original.control_type ?? '—',
        widthPercent: 12,
      },
      {
        id: 'frequency',
        columnLabel: 'Frequency',
        header: () => 'Frequency',
        cell: ({ row }) => row.original.frequency ?? '—',
        widthPercent: 12,
      },
      {
        id: 'significance',
        columnLabel: 'Significance',
        header: () => 'Significance',
        cell: ({ row }) => row.original.significance ?? '—',
        widthPercent: 12,
      },
      {
        id: 'mrc_control',
        columnLabel: 'MRC',
        header: () => 'MRC',
        cell: ({ row }) => (row.original.mrc_control ? 'Yes' : 'No'),
        widthPercent: 8,
      },
      {
        id: 'status',
        columnLabel: 'Status',
        header: () => 'Status',
        cell: ({ row }) => <StatusChip value={row.original.status} fallback="Unknown" />,
        widthPercent: 16,
      },
    ],
    []
  );

  return (
    <AppDataTable<AuditorControlInventoryRow>
      data={rows}
      columns={columns}
      loading={isLoading}
      error={isError ? error?.message ?? 'Failed to load the control inventory.' : null}
      emptyMessage="No reliance-eligible controls found."
      searchable
      searchPlaceholder="Search controls..."
      paginated
      defaultPageSize={10}
      showPageSizeSelector
      headerStart={
        <div className="flex w-full justify-end">
          <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
        </div>
      }
      topToolbar={
        <button
          type="button"
          onClick={handleExport}
          disabled={rows.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-4 w-4" />
          Export to Excel
        </button>
      }
    />
  );
};

export default ControlInventoryTable;
